// src/tools/ui.tsx
// Shared, theme-consistent building blocks for the developer tools.
import React from 'react';
import styled, { css } from 'styled-components';

export const ToolHeader = styled.div`
  margin-bottom: 1.6rem;
`;

export const ToolTitle = styled.h1`
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  svg { color: ${(p) => p.theme.colors.accentLight}; }
`;

export const ToolSubtitle = styled.p`
  color: ${(p) => p.theme.colors.muted};
  font-size: 0.95rem;
  max-width: 60ch;
  margin: 0;
`;

export const Panel = styled.div`
  background: ${(p) => p.theme.gradients.card};
  border: 1px solid ${(p) => p.theme.colors.glassBorder};
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: ${(p) => p.theme.shadows.card};
`;

export const Grid = styled.div<{ $cols?: number; $min?: string }>`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(
    ${(p) => p.$cols ?? 'auto-fit'},
    minmax(${(p) => p.$min ?? '280px'}, 1fr)
  );
`;

export const Row = styled.div<{ $gap?: string; $wrap?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.$gap ?? '0.6rem'};
  flex-wrap: ${(p) => (p.$wrap ? 'wrap' : 'nowrap')};
`;

export const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.muted};
  margin-bottom: 0.5rem;
`;

const fieldStyles = css`
  width: 100%;
  background: ${(p) => p.theme.colors.dark};
  border: 1px solid ${(p) => p.theme.colors.glassBorder};
  border-radius: 10px;
  color: ${(p) => p.theme.colors.white};
  font-family: ${(p) => p.theme.fonts.code};
  font-size: 0.9rem;
  padding: 0.7rem 0.85rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &::placeholder { color: ${(p) => p.theme.colors.muted}; }

  &:focus {
    border-color: rgba(139, 92, 246, 0.5);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
  }
`;

export const TextArea = styled.textarea`
  ${fieldStyles};
  resize: vertical;
  min-height: 180px;
  line-height: 1.55;
`;

export const Input = styled.input`
  ${fieldStyles};
`;

export const Select = styled.select`
  ${fieldStyles};
  cursor: pointer;
  appearance: none;
`;

export const Button = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: ${(p) => p.theme.fonts.main};
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.6rem 1.05rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  ${(p) =>
    p.$variant === 'ghost'
      ? css`
          background: rgba(255, 255, 255, 0.03);
          border-color: ${p.theme.colors.glassBorder};
          color: ${p.theme.colors.light};
          &:hover {
            border-color: rgba(139, 92, 246, 0.4);
            color: ${p.theme.colors.white};
          }
        `
      : css`
          background: ${p.theme.gradients.accent};
          color: #fff;
          box-shadow: ${p.theme.shadows.glow};
          &:hover {
            background: ${p.theme.gradients.accentHover};
            transform: translateY(-1px);
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const Output = styled.pre`
  ${fieldStyles};
  min-height: 120px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  margin: 0;
`;

export const ErrorText = styled.p`
  color: #f87171;
  font-size: 0.85rem;
  font-family: ${(p) => p.theme.fonts.code};
  margin: 0.5rem 0 0;
`;

export const Hint = styled.p`
  color: ${(p) => p.theme.colors.muted};
  font-size: 0.8rem;
  margin: 0.4rem 0 0;
`;

// Small copy-to-clipboard button used across tools.
export const CopyButton: React.FC<{ value: string; label?: string }> = ({
  value,
  label = 'Copy',
}) => {
  const handleCopy = () => {
    if (value) navigator.clipboard?.writeText(value);
  };
  return (
    <Button type="button" $variant="ghost" onClick={handleCopy} disabled={!value}>
      {label}
    </Button>
  );
};
